import { useState, useCallback } from 'react'
import { 
  type Address, 
  type Hex,
  type WalletClient,
  type PublicClient,
  getAddress,
  pad,
  toHex,
} from 'viem'
import { LSP0_ABI, DATA_KEYS } from '../constants'
import {
  buildControllerData,
  combinePermissions,
  type AllowedCall,
  encodeAllowedCalls,
  encodeAllowedDataKeys,
  encodeMinimalWildcardAllowedCalls,
  getControllerLabelDataKey,
  getPermissionsDataKey,
  getAllowedCallsDataKey,
  getAllowedDataKeysDataKey,
  permissionsRequireNonEmptyAllowedCalls,
} from '../utils'

export interface AuthorizationParams {
  controllerAddress: Address
  permissions: bigint
  allowedCalls?: AllowedCall[]
  allowedDataKeys?: Hex[]
  controllerLabel?: string
}

export type AuthorizationStatus = 'idle' | 'preparing' | 'pending' | 'success' | 'error'

export interface AuthorizationState {
  status: AuthorizationStatus
  txHash: Hex | null
  error: string | null
}

export function useAuthorization(
  upAddress: Address | null,
  walletClient: WalletClient | null,
  publicClient: PublicClient | null
) {
  const [state, setState] = useState<AuthorizationState>({
    status: 'idle',
    txHash: null,
    error: null,
  })

  // Check if controller is already authorized and get their permissions + allowed calls + allowed data keys
  const checkExistingController = useCallback(async (
    controllerAddress: Address
  ): Promise<{
    exists: boolean
    permissions?: Hex
    permissionsBigInt?: bigint
    allowedCallsHex?: Hex
    allowedDataKeysHex?: Hex
  }> => {
    if (!publicClient || !upAddress) {
      return { exists: false }
    }

    try {
      const permKey = `${DATA_KEYS['AddressPermissions:Permissions_prefix']}${controllerAddress.slice(2).toLowerCase()}` as Hex
      const allowedCallsKey = getAllowedCallsDataKey(controllerAddress)
      const allowedDataKeysKey = getAllowedDataKeysDataKey(controllerAddress)

      const [permissions, allowedCallsHexRaw, allowedDataKeysHexRaw] = (await publicClient.readContract({
        address: upAddress,
        abi: LSP0_ABI,
        functionName: 'getDataBatch',
        args: [[permKey, allowedCallsKey, allowedDataKeysKey]],
      })) as Hex[]

      const permissionsValue = permissions ?? ('0x' as Hex)
      const emptyBytes32 = '0x0000000000000000000000000000000000000000000000000000000000000000'
      if (permissionsValue === '0x' || permissionsValue === emptyBytes32) {
        return { exists: false }
      }

      const permissionsBigInt = BigInt(permissionsValue)
      if (permissionsBigInt === 0n) {
        return { exists: false }
      }

      const allowedCallsHex = (allowedCallsHexRaw && allowedCallsHexRaw !== '0x') ? allowedCallsHexRaw : undefined
      const allowedDataKeysHex = (allowedDataKeysHexRaw && allowedDataKeysHexRaw !== '0x') ? allowedDataKeysHexRaw : undefined

      return {
        exists: true,
        permissions: permissionsValue,
        permissionsBigInt,
        allowedCallsHex,
        allowedDataKeysHex,
      }
    } catch (err) {
      console.error('Error checking existing controller:', err)
      return { exists: false }
    }
  }, [publicClient, upAddress])

  // Get current controllers count
  const getControllersCount = useCallback(async (): Promise<number> => {
    if (!publicClient || !upAddress) return 0

    try {
      const lengthData = await publicClient.readContract({
        address: upAddress,
        abi: LSP0_ABI,
        functionName: 'getData',
        args: [DATA_KEYS['AddressPermissions[]'] as Hex],
      }) as Hex

      if (!lengthData || lengthData === '0x') return 0
      return parseInt(lengthData.slice(0, 34), 16)
    } catch (err) {
      console.error('Error getting controllers count:', err)
      return 0
    }
  }, [publicClient, upAddress])

  // Find an empty slot in the AddressPermissions[] array
  // Returns the index of an empty slot (0x0000...0000 address), or null if none found
  const findEmptySlot = useCallback(async (arrayLength: number): Promise<number | null> => {
    if (!publicClient || !upAddress || arrayLength === 0) return null

    try {
      // Read all array elements to find empty slots
      const emptyAddress = '0x0000000000000000000000000000000000000000'

      for (let i = 0; i < arrayLength; i++) {
        const indexKey = `${DATA_KEYS['AddressPermissions[]_index_prefix']}${i.toString(16).padStart(32, '0')}` as Hex
        const addressData = await publicClient.readContract({
          address: upAddress,
          abi: LSP0_ABI,
          functionName: 'getData',
          args: [indexKey],
        }) as Hex

        // Check if this slot is empty (0x or 0x0000...0000)
        if (!addressData || addressData === '0x' || addressData.toLowerCase() === emptyAddress.toLowerCase()) {
          return i
        }
      }

      return null // No empty slots found
    } catch (err) {
      console.error('Error finding empty slot:', err)
      return null
    }
  }, [publicClient, upAddress])

  // Authorize a new controller
  const authorize = useCallback(async (params: AuthorizationParams) => {
    if (!walletClient || !publicClient || !upAddress) {
      const detail = {
        hasWalletClient: !!walletClient,
        hasPublicClient: !!publicClient,
        upAddress: upAddress ?? null,
      }
      console.error('[useAuthorization] Cannot authorize: wallet not fully ready', detail)
      setState({
        status: 'error',
        txHash: null,
        error: !walletClient
          ? 'Wallet client not ready. If you just connected via WalletConnect, please wait a moment and try again.'
          : 'Wallet not connected',
      })
      return
    }

    setState({ status: 'preparing', txHash: null, error: null })

    try {
      // Check if controller already exists - if so, we'll update their permissions
      const existing = await checkExistingController(params.controllerAddress)
      const isExistingController = existing.exists

      // Get current controllers count and find empty slot (only needed for new controllers)
      let currentLength = 0
      let emptySlotIndex: number | null = null

      if (!isExistingController) {
        currentLength = await getControllersCount()
        // Check for empty slots in the array before appending
        emptySlotIndex = await findEmptySlot(currentLength)
      }

      // Build data keys and values
      const permissionsHex = combinePermissions([params.permissions])
      // `undefined` = caller did not specify (preserve on edit). `[]` = explicit empty list / clear.
      let allowedCallsHex: Hex | undefined
      if (params.allowedCalls === undefined) {
        allowedCallsHex = undefined
      } else if (params.allowedCalls.length === 0) {
        allowedCallsHex = '0x' as Hex
      } else {
        allowedCallsHex = encodeAllowedCalls(params.allowedCalls)
      }
      let allowedDataKeysHex: Hex | undefined
      if (params.allowedDataKeys === undefined) {
        allowedDataKeysHex = undefined
      } else if (params.allowedDataKeys.length === 0) {
        allowedDataKeysHex = '0x' as Hex
      } else {
        allowedDataKeysHex = encodeAllowedDataKeys(params.allowedDataKeys)
      }

      // Editing an existing controller: if the UI omits AllowedCalls / AllowedDataKeys (undefined),
      // still carry forward what is already on-chain. Otherwise we only write permissions (+ label),
      // and LSP6 rejects the update (scoped CALL / SETDATA must stay consistent with stored lists).
      // Explicit clears use [] → encodeAllowedCalls([]) === '0x' (still defined), not this path.
      if (isExistingController) {
        if (allowedCallsHex === undefined && existing.allowedCallsHex) {
          allowedCallsHex = existing.allowedCallsHex
        }
        if (allowedDataKeysHex === undefined && existing.allowedDataKeysHex) {
          allowedDataKeysHex = existing.allowedDataKeysHex
        }
      }

      // LSP6 rejects permission updates to scoped CALL / … without a non-empty AllowedCalls list.
      // If we still have no bytes (no on-chain list, empty UI, empty template), use one wildcard tuple.
      // Do not wildcard when the caller explicitly passed [] (clear restrictions).
      const permBigInt = params.permissions
      const explicitEmptyAllowedCalls =
        params.allowedCalls !== undefined && params.allowedCalls.length === 0
      if (
        permissionsRequireNonEmptyAllowedCalls(permBigInt) &&
        (allowedCallsHex === undefined || allowedCallsHex === ('0x' as Hex)) &&
        !explicitEmptyAllowedCalls
      ) {
        allowedCallsHex = encodeMinimalWildcardAllowedCalls()
      }

      const { dataKeys, dataValues } = buildControllerData(
        params.controllerAddress,
        permissionsHex,
        currentLength,
        allowedCallsHex,
        allowedDataKeysHex,
        isExistingController, // Skip array operations if controller already exists
        emptySlotIndex // Use empty slot if available, otherwise append to end
      )

      // Existing controllers: if permissions are unchanged, don't write the permissions key.
      // This avoids unnecessary CHANGEPERMISSIONS checks when we're only updating restrictions.
      if (isExistingController && existing.permissions && existing.permissions.toLowerCase() === permissionsHex.toLowerCase()) {
        const permKey = getPermissionsDataKey(params.controllerAddress)
        const idx = dataKeys.findIndex((k) => k === permKey)
        if (idx >= 0) {
          dataKeys.splice(idx, 1)
          dataValues.splice(idx, 1)
        }
      }

      const label = params.controllerLabel?.trim()
      if (label) {
        dataKeys.push(getControllerLabelDataKey(params.controllerAddress))
        dataValues.push(toHex(label) as Hex)
      }

      console.log('Authorization data:', {
        upAddress,
        controllerAddress: params.controllerAddress,
        permissions: permissionsHex,
        dataKeys,
        dataValues,
      })

      setState({ status: 'pending', txHash: null, error: null })

      // New controllers can use one setDataBatch including label.
      // Existing-controller updates can revert if AddressPermissions keys are mixed together.
      // Use strict order: (1) restrictions, (2) permissions, (3) label.
      let hash: Hex
      if (isExistingController) {
        const permKey = getPermissionsDataKey(params.controllerAddress)
        const allowedCallsKey = getAllowedCallsDataKey(params.controllerAddress)
        const allowedDataKeysKey = getAllowedDataKeysDataKey(params.controllerAddress)
        const labelKey = getControllerLabelDataKey(params.controllerAddress)

        const valueByKey = new Map<Hex, Hex>()
        for (let i = 0; i < dataKeys.length; i++) {
          valueByKey.set(dataKeys[i], dataValues[i])
        }

        let lastHash: Hex | null = null

        const restrictionsKeys: Hex[] = []
        const restrictionsValues: Hex[] = []
        for (const k of [allowedCallsKey, allowedDataKeysKey]) {
          const v = valueByKey.get(k)
          if (v !== undefined) {
            restrictionsKeys.push(k)
            restrictionsValues.push(v)
          }
        }
        if (restrictionsKeys.length > 0) {
          lastHash = await walletClient.writeContract({
            address: upAddress,
            abi: LSP0_ABI,
            functionName: 'setDataBatch',
            args: [restrictionsKeys, restrictionsValues],
            account: upAddress,
            chain: walletClient.chain,
          })
          await publicClient.waitForTransactionReceipt({ hash: lastHash })
        }

        const permVal = valueByKey.get(permKey)
        if (permVal !== undefined) {
          lastHash = await walletClient.writeContract({
            address: upAddress,
            abi: LSP0_ABI,
            functionName: 'setDataBatch',
            args: [[permKey], [permVal]],
            account: upAddress,
            chain: walletClient.chain,
          })
          await publicClient.waitForTransactionReceipt({ hash: lastHash })
        }

        const labelVal = valueByKey.get(labelKey)
        if (labelVal !== undefined && label) {
          lastHash = await walletClient.writeContract({
            address: upAddress,
            abi: LSP0_ABI,
            functionName: 'setDataBatch',
            args: [[labelKey], [labelVal]],
            account: upAddress,
            chain: walletClient.chain,
          })
          await publicClient.waitForTransactionReceipt({ hash: lastHash })
        }

        if (!lastHash) {
          throw new Error('No controller data to write')
        }
        hash = lastHash
      } else {
        hash = await walletClient.writeContract({
          address: upAddress,
          abi: LSP0_ABI,
          functionName: 'setDataBatch',
          args: [dataKeys, dataValues],
          account: upAddress,
          chain: walletClient.chain,
        })
      }

      console.log('Transaction hash:', hash)

      const receipt = await publicClient.waitForTransactionReceipt({ hash })

      if (receipt.status === 'success') {
        setState({
          status: 'success',
          txHash: hash,
          error: null,
        })
      } else {
        console.error('[useAuthorization] Transaction failed on-chain', { hash, status: receipt.status })
        setState({
          status: 'error',
          txHash: hash,
          error: 'Transaction failed',
        })
      }
    } catch (err) {
      console.error('Authorization error:', err)
      setState({
        status: 'error',
        txHash: null,
        error: err instanceof Error ? err.message : 'Authorization failed',
      })
    }
  }, [walletClient, publicClient, upAddress, checkExistingController, getControllersCount])

  // Strictly remove a controller:
  // 1) clear permissions and restrictions keys
  // 2) clear AddressPermissions[] slot
  // 3) compact AddressPermissions[] length from trailing empty slots
  const removeControllerStrict = useCallback(async (controllerAddress: Address) => {
    if (!walletClient || !publicClient || !upAddress) {
      setState({
        status: 'error',
        txHash: null,
        error: !walletClient
          ? 'Wallet client not ready. If you just connected via WalletConnect, please wait a moment and try again.'
          : 'Wallet not connected',
      })
      return
    }

    setState({ status: 'preparing', txHash: null, error: null })

    try {
      const currentLength = await getControllersCount()

      const slotZeroAddress = '0x0000000000000000000000000000000000000000' as Hex
      const zeroBytes32 = pad(toHex(0), { size: 32 })
      const zeroBytes16 = pad(toHex(0), { size: 16 })

      const permissionKey = `${DATA_KEYS['AddressPermissions:Permissions_prefix']}${controllerAddress.slice(2).toLowerCase()}` as Hex
      const allowedCallsKey = `${DATA_KEYS['AddressPermissions:AllowedCalls_prefix']}${controllerAddress.slice(2).toLowerCase()}` as Hex
      const allowedDataKeysKey = `${DATA_KEYS['AddressPermissions:AllowedERC725YDataKeys_prefix']}${controllerAddress.slice(2).toLowerCase()}` as Hex
      const labelKey = getControllerLabelDataKey(controllerAddress)

      const dataKeys: Hex[] = [permissionKey, allowedCallsKey, allowedDataKeysKey, labelKey]
      const dataValues: Hex[] = [zeroBytes32, '0x' as Hex, '0x' as Hex, '0x' as Hex]

      if (currentLength > 0) {
        const indexKeys: Hex[] = Array.from(
          { length: currentLength },
          (_, i) => `${DATA_KEYS['AddressPermissions[]_index_prefix']}${i.toString(16).padStart(32, '0')}` as Hex
        )

        const indexValues = (await publicClient.readContract({
          address: upAddress,
          abi: LSP0_ABI,
          functionName: 'getDataBatch',
          args: [indexKeys],
        })) as Hex[]

        const normalizedAddresses: Array<Address | null> = indexValues.map((value) => {
          if (!value || value === '0x') return null
          const last40 = value.slice(-40)
          if (!last40 || /^0{40}$/.test(last40)) return null
          try {
            return getAddress((`0x${last40}`) as Hex)
          } catch {
            return null
          }
        })

        const targetIndex = normalizedAddresses.findIndex(
          (addr) => addr?.toLowerCase() === controllerAddress.toLowerCase()
        )

        if (targetIndex >= 0) {
          dataKeys.push(indexKeys[targetIndex])
          dataValues.push(slotZeroAddress)
        }

        let lastNonEmptyIndex = -1
        for (let i = normalizedAddresses.length - 1; i >= 0; i--) {
          if (i === targetIndex) continue
          if (normalizedAddresses[i]) {
            lastNonEmptyIndex = i
            break
          }
        }

        const nextLength = lastNonEmptyIndex >= 0 ? lastNonEmptyIndex + 1 : 0
        if (nextLength !== currentLength) {
          dataKeys.push(DATA_KEYS['AddressPermissions[]'] as Hex)
          dataValues.push(nextLength === 0 ? zeroBytes16 : pad(toHex(nextLength), { size: 16 }))
        }
      }

      setState({ status: 'pending', txHash: null, error: null })

      const hash = await walletClient.writeContract({
        address: upAddress,
        abi: LSP0_ABI,
        functionName: 'setDataBatch',
        args: [dataKeys, dataValues],
        account: upAddress,
        chain: walletClient.chain,
      })

      const receipt = await publicClient.waitForTransactionReceipt({ hash })
      if (receipt.status === 'success') {
        setState({ status: 'success', txHash: hash, error: null })
      } else {
        setState({ status: 'error', txHash: hash, error: 'Transaction failed' })
      }
    } catch (err) {
      console.error('Strict controller removal error:', err)
      setState({
        status: 'error',
        txHash: null,
        error: err instanceof Error ? err.message : 'Controller removal failed',
      })
    }
  }, [walletClient, publicClient, upAddress, getControllersCount])

  // Reset state
  const reset = useCallback(() => {
    setState({
      status: 'idle',
      txHash: null,
      error: null,
    })
  }, [])

  return {
    ...state,
    authorize,
    removeControllerStrict,
    reset,
    checkExistingController,
    findEmptySlot,
  }
}
